
    // IMPORTS
    import { DbConstants } from '@/const'
    import { useShoppingCartStore } from '@/store/shoppingCart'
    import { defineComponent, PropType, computed, ref, reactive, nextTick } from 'vue'
    import { Button, ButtonGroup } from '@progress/kendo-vue-buttons'
    import { useSparePartsStore } from '@/store/spareParts'
    import { DialogActionsBar } from '@progress/kendo-vue-dialogs'
    import AppDialog from '@/components/common/app-dialog.vue'
    import { Input } from '@progress/kendo-vue-inputs'
    import { buildLocalization, StringUtil } from '@/util'
    import { useEquipmentStore } from '@/store/equipment'
    import { NotificationDTO, useNotificationStore } from '@/store/notification'

    export default defineComponent({
        name: 'purchase-button',

        components: {
            'k-button': Button,
            'k-input': Input,
            ButtonGroup,
            AppDialog,
            DialogActionsBar
        },

        props: {
            materialNumber: { type: Number as PropType<number>, required: true },
            disabled: { type: Boolean as PropType<boolean>, required: false, default: () => false },
            fixedCount: { type: Number as PropType<number | null>, required: false, default: () => null }
        },

        setup(props) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const shoppingCartStore = useShoppingCartStore()
            const sparePartStore = useSparePartsStore()
            const equipmentStore = useEquipmentStore()
            const notificationStore = useNotificationStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const isUpdating = ref(false)
            const showDialog = ref(false)
            const dialogData = reactive({
                serialNumbers: [] as string[],
                emphasizedIndex: null as (number | null)
            })

            const publicData = { isUpdating, showDialog, dialogData }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const hasLoaded = computed(() => sparePartStore.spareParts != null)
            const materialNo = computed(() => props.materialNumber)
            const bomHeader = computed(() => sparePartStore.sparePartMap?.get(materialNo.value.toString()))
            const material = computed(() => bomHeader.value?.material)
            const isHiddenMaterial = computed(() => material.value?.hiddenSparePart ?? false)
            const sparePartStatus = computed(() => material.value?.sparePartStatus ?? '')
            const requiresSerialNumber = computed(() => material.value?.sparePartRequiringSerialNumber ?? false)
            const shoppingCart = computed(() => shoppingCartStore.shoppingCart)
            const shoppingCartItem = computed(() => shoppingCart.value?.items.find(item => item.materialNumber === materialNo.value))
            const purchaseCount = computed(() => props.fixedCount ?? shoppingCartItem.value?.count ?? 0)
            const allowPurchase = computed(() => !isHiddenMaterial.value &&
                DbConstants.sparePartStatusesAllowingPurchase.find(status => status === sparePartStatus.value) != null
            )
            const disabledView = computed(() => props.disabled || isUpdating.value || props.fixedCount != null)

            const computedData = {
                hasLoaded,
                material,
                isHiddenMaterial,
                sparePartStatus,
                requiresSerialNumber,
                purchaseCount,
                allowPurchase,
                disabledView
            }

            /********************************************************************************************************************
             PUBLIC METHODS AND EVENT HANDLERS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function orderItem(count: number) {
                if (materialNo.value) {
                    isUpdating.value = true

                    shoppingCartStore.update(materialNo.value, count)
                        .finally(() => isUpdating.value = false)
                }
            }

            /** Remove the entire line from the cart, with an "Undo" notification that can restore count + serial numbers. */
            function deleteWithUndo() {
                if (!materialNo.value) return

                // Snapshot before removing, so Undo can restore the exact state.
                const snapshotCount = purchaseCount.value
                const snapshotSerialNumbers = [...(shoppingCartItem.value?.serialNumbers ?? [])]
                const snapshotMaterialNo = materialNo.value

                isUpdating.value = true
                shoppingCartStore.update(snapshotMaterialNo, -snapshotCount)
                    .then(() => {
                        const notification = new NotificationDTO(localize('ItemRemovedFromCart'), 'info', 10000)
                            .withAction(localize('Undo'), () => {
                                shoppingCartStore.update(snapshotMaterialNo, snapshotCount)
                                    .then(() => {
                                        if (snapshotSerialNumbers.length > 0) {
                                            shoppingCartStore.updateSerialNumbers(snapshotMaterialNo, snapshotSerialNumbers)
                                        }
                                    })
                            })
                        notificationStore.addNotification(notification)
                    })
                    .finally(() => isUpdating.value = false)
            }

            function openDialog() {
                dialogData.serialNumbers = [ ...shoppingCartItem.value?.serialNumbers ?? [] ]
                dialogData.emphasizedIndex = null

                if (dialogData.serialNumbers.length == 0) {
                    addSerialNumber()
                }

                showDialog.value = true
            }

            function closeDialog() {
                showDialog.value = false
            }

            function addSerialNumber() {
                dialogData.serialNumbers.push(newSerialNumber())
                dialogData.emphasizedIndex = dialogData.serialNumbers.length - 1

                nextTick(() => {
                    nextTick().then(() => {
                        const element = document.getElementById(`txt-sn-${dialogData.emphasizedIndex}`)
                        element?.focus()
                    })})
            }

            function newSerialNumber(): string {
                return equipmentStore.activeSerialNo.value ?? ''
            }

            function removeSerialNumber(index: number) {
                dialogData.serialNumbers.splice(index, 1)
            }

            function submitDialog() {
                if (materialNo.value) {
                    isUpdating.value = true
                    closeDialog()
                    const newSerialNumbers = dialogData.serialNumbers.filter(sn => StringUtil.hasContent(sn))
                    shoppingCartStore.updateSerialNumbers(materialNo.value, newSerialNumbers)
                        .finally(() => isUpdating.value = false)
                }
            }

            const publicMethods = { localize, orderItem, deleteWithUndo, openDialog, closeDialog, submitDialog, addSerialNumber, removeSerialNumber }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
